export interface TimezoneOption {
  value: string
  label: string
}

// Dozens of IANA zones share the same current offset (e.g. Rome, Berlin and
// Madrid are all UTC+02:00 in summer). Rendering one row per zone made the
// Select popover mount ~400 items — slow to open, and the Radix/Reka
// collection registration for that many items visibly delayed the trigger's
// display value after picking one. Grouping by offset cuts it to ~40 rows.
const MAX_CITIES_SHOWN = 4

// Priority order so the label leads with recognizable cities instead of
// whichever zone sorts first alphabetically (Africa/Bujumbura beats Rome).
const MAJOR_ZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Rome',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Warsaw',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Prague',
  'Europe/Budapest',
  'Europe/Athens',
  'Europe/Bucharest',
  'Europe/Helsinki',
  'Europe/Kyiv',
  'Europe/Istanbul',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Sao_Paulo',
  'America/Mexico_City',
  'America/Bogota',
  'America/Toronto',
  'America/Vancouver',
  'Pacific/Honolulu',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Seoul',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Asia/Jakarta',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
] as const

/** Current UTC offset of an IANA zone, in minutes (e.g. +120 for CEST). */
export function getUtcOffsetMinutes(zone: string, reference: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    timeZoneName: 'shortOffset',
  }).formatToParts(reference)
  const raw = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT+0'
  const match = /GMT([+-]\d+)(?::(\d+))?/.exec(raw)
  if (!match) return 0

  const sign = match[1]?.startsWith('-') ? -1 : 1
  const hours = Math.abs(Number(match[1] ?? 0))
  const minutes = Number(match[2] ?? 0)
  return sign * (hours * 60 + minutes)
}

function formatOffset(totalMinutes: number): string {
  const sign = totalMinutes < 0 ? '-' : '+'
  const abs = Math.abs(totalMinutes)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `UTC${sign}${hh}:${mm}`
}

function cityName(zone: string): string {
  return (zone.split('/').pop() ?? zone).replace(/_/g, ' ')
}

function relevanceRank(zone: string): number {
  const index = MAJOR_ZONES.indexOf(zone as (typeof MAJOR_ZONES)[number])
  return index === -1 ? Number.MAX_SAFE_INTEGER : index
}

function byRelevance(zones: string[]): string[] {
  return [...zones].sort((a, b) => relevanceRank(a) - relevanceRank(b) || a.localeCompare(b))
}

function pickRepresentative(zones: string[]): string {
  return byRelevance(zones)[0] ?? 'UTC'
}

function buildLabel(minutes: number, zones: string[]): string {
  const cities = Array.from(new Set(byRelevance(zones).map(cityName)))
  const shown = cities.slice(0, MAX_CITIES_SHOWN)
  const remaining = cities.length - shown.length
  const suffix = remaining > 0 ? ` +${remaining}` : ''
  return `(${formatOffset(minutes)}) ${shown.join(' / ')}${suffix}`
}

let cache: TimezoneOption[] | null = null

/**
 * One option per *distinct current UTC offset*, not per IANA zone — grouping
 * ~400 zones into ~40 rows. The stored `value` is still a real IANA zone
 * (needed for correct DST math down the line), picked as a recognizable
 * representative of its group; the label lists a few of its cities.
 */
export function getTimezoneOptions(): TimezoneOption[] {
  if (cache) return cache

  const now = new Date()
  const groups = new Map<number, string[]>()
  for (const zone of Intl.supportedValuesOf('timeZone')) {
    const minutes = getUtcOffsetMinutes(zone, now)
    const list = groups.get(minutes)
    if (list) list.push(zone)
    else groups.set(minutes, [zone])
  }

  cache = Array.from(groups.entries())
    .sort(([a], [b]) => a - b)
    .map(([minutes, zones]) => ({
      value: pickRepresentative(zones),
      label: buildLabel(minutes, zones),
    }))

  return cache
}
